import React, { useEffect, useState, useRef } from 'react';
import { SearchResultsList, SearchResultsListTitle } from '../../list';
import { Pagination } from '../../../pagination';
import { SearchResultsProps } from '../types';
import { getOrderAssistUIString } from '../../../../consts/strings';
import { OrderAssistOption } from '../../../../types/results';

import './TeladocSearchResults.less';

const ITEMS_PER_PAGE = 10;

export const TeladocSearchResults: React.FC<
  Omit<SearchResultsProps, 'source' | 'disclaimers' | 'changePage'>
> = ({
  results,
  referralViewedPayload,
  currentSearchState,
  costResultStatus,
  sendOutcome,
  onProviderSelect,
  getSelectionType,
  onProviderAddressChange,
  isReferralRequestRequired,
  isReadonlyMode,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedResults, setPaginatedResults] = useState<OrderAssistOption[]>([]);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPaginatedResults(results?.searchResults?.slice(startIndex, endIndex));
  }, [results.searchResults, currentPage]);

  // Scroll to top when page changes
  useEffect(() => {
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [currentPage]);

  const totalCount = results.searchResults.length;
  const totalPages = Math.ceil(results.searchResults.length / ITEMS_PER_PAGE);

  const hasNextPage = currentPage < totalPages;

  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  return (
    <div className="teladoc-search-results" ref={searchResultsRef}>
      <div className="teladoc-search-results__disclaimer">
        Please verify that your provider and services are in-network with your insurance before your
        visit. Insurance networks change regularly, and there is no guarantee the information
        provided here will in all cases be accurate or up-to-date.
      </div>
      <SearchResultsList
        titleElement={
          <SearchResultsListTitle
            title={
              <>
                {getOrderAssistUIString('search_results_out_of_network_results_list_title')}
                <span style={{ fontWeight: 'normal' }}> ({totalCount})</span>
              </>
            }
            withUhcPremiumPhysicianProgram={false}
          />
        }
        results={paginatedResults}
        extraInfo={{ distanceInfo: results?.distanceInfo }}
        getSelectionType={getSelectionType}
        referralViewedPayload={referralViewedPayload}
        onProviderSelect={onProviderSelect}
        sendOutcome={sendOutcome}
        currentSearchState={currentSearchState}
        costResultStatus={costResultStatus}
        hasPreferredList={false}
        onProviderAddressChange={onProviderAddressChange}
        isReferralRequestRequired={isReferralRequestRequired}
        isReadonlyMode={isReadonlyMode}
        shouldDisplayNewPatientsIcon={false}
      />

      <div className="search-results__pagination">
        <Pagination
          currentPage={currentPage}
          goPrevious={() => handlePreviousPage()}
          goNext={() => handleNextPage()}
          goNextDisable={!hasNextPage}
        />
      </div>
    </div>
  );
};
